import * as THREE from "three";
import { GASBAGS, INCOMERS, fishAlive, fishOf, isIncomer, schoolHash, type CreatureState, type IncomerKind, type School, type Vec2 } from "@dyson/shared";
import { CARRIER_BOX, EEL_BOX, SPIDER_BOX, WARDEN_BOX, drawCarrier, drawEel, drawSpider, drawWarden, setGlowLayer } from "./nightArt";
import { idHash } from "./rig/Rig";
import { RigMesh } from "./rig/RigMesh";
import { GasbagAnimator, gasbagModel, gasbagPalette } from "./rig/gasbag";
import { grazerKit, type GrazerKit } from "./rig/grazer";
import { FISH_SCALE, FishAnimator, fishLook, fishModel, fishPalette, fishVariant } from "./rig/skyfish";
import { WHALE_GLOWS, WHALE_MODELS, WhaleAnimator, whalePalette, whaleVariant, type WhaleVariant } from "./rig/whale";

/**
 * How the creatures are drawn: each is a rig (see rig/) posed every frame
 * by its animator, and these classes only place it in the world: where it
 * is, which way it faces, how big it's grown and how lit it is.
 */

/** How a creature is drawn, whatever kind: given where it is this frame (see Renderer.updateCreatures) and how lit it is there. */
export interface CreatureView {
  readonly group: THREE.Group;
  /** `origin`: the grid origin of the rock it's by (where it's drawn from), or (0, 0) if none; `local` where it is from there. */
  update(c: CreatureState, origin: Vec2, local: Vec2, light: number, dt: number): void;
  dispose(): void;
}

export function creatureView(kind: CreatureState["kind"], id: string): CreatureView {
  if (isIncomer(kind)) return new NightMesh(kind, id);
  if (kind === "gasbag") return new GasbagMesh(id);
  if (kind === "grazer") return new GrazerMesh(id);
  if (kind === "skyfish") return new SkyfishMesh();
  return new WhaleMesh(id);
}

/** Growing in as it turns up, over `over` seconds. */
const grown = (age: number, over: number) => {
  const g = Math.min(1, age / over);
  return g * g * (3 - 2 * g);
};

/**
 * Where a creature by a rock is drawn: eased toward where it's sent (which
 * comes 20 times a second) from that rock, so it moves smoothly and with the
 * rock. Changing rocks (a gas bag off to another tree), it carries on from
 * where it was drawn.
 */
class Follow {
  private rock: string | undefined;
  private readonly at = new THREE.Vector2();
  private started = false;
  /** How fast it's drawn moving against its rock (blocks/s). */
  speed = 0;

  step(c: CreatureState, origin: Vec2, local: Vec2, dt: number): THREE.Vector2 {
    if (!this.started || Math.hypot(local.x - this.at.x, local.y - this.at.y) > 30) {
      this.at.set(local.x, local.y);
      this.started = true;
    } else if (c.rock !== this.rock) this.at.set(this.at.x + this.lastOrigin.x - origin.x, this.at.y + this.lastOrigin.y - origin.y);
    this.rock = c.rock;
    this.lastOrigin = { x: origin.x, y: origin.y };
    const k = 1 - Math.exp(-dt * 10);
    const dx = (local.x - this.at.x) * k;
    const dy = (local.y - this.at.y) * k;
    this.at.x += dx;
    this.at.y += dy;
    const now = dt > 0 ? Math.hypot(dx, dy) / dt : 0;
    this.speed += (now - this.speed) * (1 - Math.exp(-dt * 6));
    return new THREE.Vector2(origin.x + this.at.x, origin.y + this.at.y);
  }

  private lastOrigin: Vec2 = { x: 0, y: 0 };
}

// ------------------------------------------------------------ whales

/** A whale (one of three kinds: see rig/whale.ts), side on: it can only face left or right, and rolls a little toward where it's heading. Angry, its glows burn red. */
class WhaleMesh implements CreatureView {
  readonly group = new THREE.Group();
  private readonly rig: RigMesh;
  private readonly anim: WhaleAnimator;
  private readonly variant: WhaleVariant;
  private angry = false;

  constructor(id: string) {
    this.variant = whaleVariant(id);
    this.rig = new RigMesh(WHALE_MODELS[this.variant], whalePalette(this.variant, false));
    this.anim = new WhaleAnimator(idHash(id));
    this.group.add(this.rig.group);
  }

  update(c: CreatureState, _origin: Vec2, _local: Vec2, _light: number, dt: number): void {
    this.group.position.set(c.position.x, c.position.y, -0.5);
    const left = Math.cos(c.angle) < 0;
    this.group.scale.x = left ? -1 : 1;
    this.group.rotation.z = (left ? -1 : 1) * Math.sin(c.angle) * 0.5;
    if (c.angry !== this.angry) {
      this.angry = c.angry;
      const palette = whalePalette(this.variant, c.angry);
      for (const g of WHALE_GLOWS) this.rig.setGlow(g, palette[g]!);
    }
    this.rig.pose(this.anim.step(dt, { angry: c.angry }), 1);
  }

  dispose(): void {
    this.rig.dispose();
  }
}

// ------------------------------------------------------------ gas bags

/** A gas bag (see rig/gasbag.ts): turning (slowly: it's big) to face where it's drifting, nose down a little going forward. */
class GasbagMesh implements CreatureView {
  readonly group = new THREE.Group();
  private readonly face = new THREE.Group();
  private readonly rig: RigMesh;
  private readonly anim: GasbagAnimator;
  private readonly follow = new Follow();
  private age = 0;
  private lean = 0;
  private facing = 1;

  constructor(id: string) {
    this.rig = new RigMesh(gasbagModel(id), gasbagPalette(id));
    this.anim = new GasbagAnimator(idHash(id));
    this.face.add(this.rig.group);
    this.group.add(this.face);
  }

  update(c: CreatureState, origin: Vec2, local: Vec2, light: number, dt: number): void {
    this.age += dt;
    const at = this.follow.step(c, origin, local, dt);
    this.group.position.set(at.x, at.y, 0.6);
    this.group.scale.setScalar(grown(this.age, 1.2));
    const going = Math.min(1, this.follow.speed / GASBAGS.speed);
    const want = Math.cos(c.angle) < 0 ? -1 : 1;
    if (going > 0.2) this.facing += (want - this.facing) * (1 - Math.exp(-dt * 2.5));
    this.face.scale.x = Math.abs(this.facing) < 0.12 ? 0.12 * Math.sign(this.facing || 1) : this.facing;
    const lean = c.eating ? 0 : -going * 0.12 * Math.sign(this.facing) + Math.sin(c.angle) * going * 0.1 * Math.sign(this.facing);
    this.lean += (lean - this.lean) * (1 - Math.exp(-dt * 2));
    this.group.rotation.z = this.lean;
    this.rig.pose(this.anim.step(dt, { going, eating: !!c.eating }), 0.4 + 0.6 * Math.min(1, light));
  }

  dispose(): void {
    this.rig.dispose();
  }
}

// ------------------------------------------------------------ skyfish

/**
 * A school of skyfish (see fishOf and rig/skyfish.ts): one rig a fish, all
 * of one kind and colourway by the school's seed. They swim round the rock
 * together, beating faster when they scatter; a fish caught is gone from it.
 */
class SkyfishMesh implements CreatureView {
  readonly group = new THREE.Group();
  private readonly fish: { group: THREE.Group; rig: RigMesh; anim: FishAnimator; phase: number }[] = [];
  private school: School | null = null;
  private size = 1;
  private time = Math.random() * 10;
  private age = 0;

  /** Make the fish, once the school's known (its kind, colours and how many it has). */
  private build(s: School): void {
    const variant = fishVariant(schoolHash(s.seed, 2));
    const model = fishModel(variant);
    const palette = fishPalette(fishLook(schoolHash(s.seed, 1)));
    this.size = FISH_SCALE[variant];
    for (let i = 0; i < s.n; i++) {
      const rig = new RigMesh(model, palette);
      const group = new THREE.Group();
      group.add(rig.group);
      this.group.add(group);
      const phase = schoolHash(s.seed, 70 + i) * Math.PI * 2;
      this.fish.push({ group, rig, anim: new FishAnimator(phase), phase });
    }
  }

  update(c: CreatureState, origin: Vec2, local: Vec2, light: number, dt: number): void {
    const sent = c.school;
    if (!sent) return;
    this.time += dt;
    this.age += dt;
    if (!this.school) {
      this.school = { ...sent };
      this.build(sent);
    }
    // Eased toward where it's sent (20 times a second), so it swims smoothly.
    const s = this.school;
    const k = 1 - Math.exp(-dt * 8);
    s.round = Math.abs(sent.round - s.round) > 1 ? sent.round : s.round + (sent.round - s.round) * k;
    s.scatter += (sent.scatter - s.scatter) * k;
    s.out = sent.out;
    s.alive = sent.alive;
    this.group.position.set(origin.x + local.x, origin.y + local.y, 0.25);
    const grow = grown(this.age, 1);
    const l = 0.45 + 0.55 * Math.min(1, light);
    const beat = 9 + s.scatter * 9;
    this.fish.forEach((f, i) => {
      f.group.visible = fishAlive(s, i);
      if (!f.group.visible) return;
      const fish = fishOf(s, i);
      f.group.position.set(fish.at.x, fish.at.y, i * 0.001);
      // Side on, it can't swim upside down: heading left, it's mirrored top to bottom.
      const left = Math.cos(fish.heading) < 0;
      f.group.rotation.z = fish.heading + Math.sin(this.time * beat * 0.5 + f.phase) * 0.04;
      const size = fish.size * grow * this.size;
      f.group.scale.set(size, size * (left ? -1 : 1), 1);
      f.rig.pose(f.anim.step(dt, { scatter: s.scatter }), l);
    });
  }

  dispose(): void {
    for (const f of this.fish) f.rig.dispose();
  }
}

// ------------------------------------------------------------ grazers

/** A grazer (a longneck or a mossback: see rig/grazer.ts), its feet on the ground and up turned to the rock's up, facing the way it walks. */
class GrazerMesh implements CreatureView {
  readonly group = new THREE.Group();
  private readonly turn = new THREE.Group();
  private readonly rig: RigMesh;
  private readonly kit: GrazerKit;
  private readonly follow = new Follow();
  private age = 0;
  private up = 0;
  private facing = 1;

  constructor(id: string) {
    this.kit = grazerKit(id);
    this.rig = new RigMesh(this.kit.model, this.kit.palette);
    this.turn.add(this.rig.group);
    this.group.add(this.turn);
  }

  update(c: CreatureState, origin: Vec2, local: Vec2, light: number, dt: number): void {
    this.age += dt;
    const at = this.follow.step(c, origin, local, dt);
    this.group.position.set(at.x, at.y, 0);
    this.group.scale.setScalar(grown(this.age, 0.8));
    // Up turns with the ground, eased so it doesn't snap at a corner.
    const up = c.angle - Math.PI / 2;
    this.up += Math.atan2(Math.sin(up - this.up), Math.cos(up - this.up)) * (1 - Math.exp(-dt * 8));
    this.group.rotation.z = this.up;
    this.facing += ((c.facing ?? 1) - this.facing) * (1 - Math.exp(-dt * 12));
    this.turn.scale.x = Math.abs(this.facing) < 0.15 ? 0.15 * Math.sign(this.facing || 1) : this.facing;
    this.rig.pose(this.kit.animator.step(dt, { speed: this.follow.speed, eating: !!c.eating }), Math.min(1, light));
  }

  dispose(): void {
    this.rig.dispose();
  }
}

// ------------------------------------------------------------ incomers

/** Texture pixels per block of an incomer's drawing. */
const NIGHT_PX = 26;
/** How often an incomer's drawing is redone (s): its painters animate in canvas, not on the GPU. */
const NIGHT_REDRAW = 1 / 24;

/** Each incomer's painter, the box it draws in (blocks, from its origin), how big it's drawn, and whether it walks on a rock (feet at the origin) or flies. */
const NIGHT_LOOKS: Record<IncomerKind, { box: { x0: number; x1: number; y0: number; y1: number }; scale: number; walks: boolean }> = {
  spider: { box: SPIDER_BOX, scale: 1.3, walks: true },
  warden: { box: WARDEN_BOX, scale: 1.5, walks: true },
  carrier: { box: CARRIER_BOX, scale: 1, walks: false },
  eel: { box: EEL_BOX, scale: 1, walks: false },
};

/**
 * Something from outside the sphere (see nightArt.ts): its painter redrawn
 * to a canvas a couple of dozen times a second, on a quad, with its lenses
 * and arcs drawn again on a second, additive quad bright enough to bloom.
 * Walkers stand on their rock and turn with it, as grazers do; fliers turn
 * to where they're heading (an eel) or hang square to the rock under them (a
 * carrier). Stalled by the light come back, it freezes, its lenses dead.
 */
class NightMesh implements CreatureView {
  readonly group = new THREE.Group();
  private readonly turn = new THREE.Group();
  private readonly canvas = document.createElement("canvas");
  private readonly glowCanvas = document.createElement("canvas");
  private readonly ctx: CanvasRenderingContext2D;
  private readonly glowCtx: CanvasRenderingContext2D;
  private readonly texture: THREE.CanvasTexture;
  private readonly glowTexture: THREE.CanvasTexture;
  private readonly owned: { dispose(): void }[] = [];
  private readonly follow = new Follow();
  private readonly look: (typeof NIGHT_LOOKS)[IncomerKind];
  private readonly at = new THREE.Vector2();
  private placed = false;
  private age = 0;
  private t: number;
  private untilDraw = 0;
  private up = 0;
  private facing = 1;
  /** How long it's been at its work (busy), and since it stopped. */
  private busyFor = 0;
  private sinceBusy = 99;
  private speed = 0;

  constructor(private readonly kind: IncomerKind, id: string) {
    this.look = NIGHT_LOOKS[kind];
    this.t = idHash(id) * 100;
    const { box, scale } = this.look;
    const w = box.x1 - box.x0;
    const h = box.y1 - box.y0;
    for (const c of [this.canvas, this.glowCanvas]) {
      c.width = Math.ceil(w * NIGHT_PX);
      c.height = Math.ceil(h * NIGHT_PX);
    }
    this.ctx = this.canvas.getContext("2d")!;
    this.glowCtx = this.glowCanvas.getContext("2d")!;
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.glowTexture = new THREE.CanvasTexture(this.glowCanvas);
    const geometry = new THREE.PlaneGeometry(w * scale, h * scale).translate(((box.x0 + box.x1) / 2) * scale, ((box.y0 + box.y1) / 2) * scale, 0);
    const body = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ map: this.texture, transparent: true, depthWrite: false }));
    const glow = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ map: this.glowTexture, color: new THREE.Color(2.4, 2.4, 2.4), transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, fog: false }));
    glow.position.z = 0.01;
    this.owned.push(geometry, body.material, glow.material, this.texture, this.glowTexture);
    this.turn.add(body, glow);
    this.group.add(this.turn);
  }

  update(c: CreatureState, origin: Vec2, local: Vec2, light: number, dt: number): void {
    const on = !c.stalled;
    this.age += dt;
    if (on) this.t += dt;
    if (c.busy) {
      this.busyFor += dt;
      this.sinceBusy = 0;
    } else {
      if (this.busyFor > 0) this.sinceBusy = 0;
      this.busyFor = 0;
      this.sinceBusy += dt;
    }
    // Where it's drawn: walkers from their rock (as grazers are), fliers eased toward where they're sent.
    let x: number;
    let y: number;
    if (this.look.walks) {
      const p = this.follow.step(c, origin, local, dt);
      x = p.x;
      y = p.y;
      this.speed = this.follow.speed;
    } else {
      if (!this.placed || Math.hypot(c.position.x - this.at.x, c.position.y - this.at.y) > 30) this.at.set(c.position.x, c.position.y);
      this.placed = true;
      const k = 1 - Math.exp(-dt * 10);
      this.at.x += (c.position.x - this.at.x) * k;
      this.at.y += (c.position.y - this.at.y) * k;
      x = this.at.x;
      y = this.at.y;
      this.speed += (Math.hypot(c.velocity.x, c.velocity.y) - this.speed) * (1 - Math.exp(-dt * 4));
    }
    this.group.position.set(x, y, this.kind === "eel" ? 0.4 : 0.55);
    this.group.scale.setScalar(grown(this.age, 0.6));
    if (this.kind === "eel") {
      // Head first toward where it's going (into the hull, latched), never upside down.
      const want = c.angle;
      this.up += Math.atan2(Math.sin(want - this.up), Math.cos(want - this.up)) * (1 - Math.exp(-dt * 6));
      this.group.rotation.z = this.up;
      this.turn.scale.set(1, Math.cos(this.up) < 0 ? -1 : 1, 1);
    } else {
      // Square to the ground under it, eased so it doesn't snap at a corner.
      const want = c.angle - Math.PI / 2;
      this.up += Math.atan2(Math.sin(want - this.up), Math.cos(want - this.up)) * (1 - Math.exp(-dt * 8));
      this.group.rotation.z = this.up;
      this.facing += ((c.facing ?? 1) - this.facing) * (1 - Math.exp(-dt * 10));
      this.turn.scale.x = Math.abs(this.facing) < 0.15 ? 0.15 * Math.sign(this.facing || 1) : this.facing;
    }
    this.untilDraw -= dt;
    if (this.untilDraw > 0) return;
    this.untilDraw = NIGHT_REDRAW;
    this.draw(c, on, 0.35 + 0.65 * Math.min(1, light));
  }

  private draw(c: CreatureState, on: boolean, l: number): void {
    const { box } = this.look;
    for (const ctx of [this.ctx, this.glowCtx]) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      ctx.setTransform(NIGHT_PX, 0, 0, -NIGHT_PX, -box.x0 * NIGHT_PX, box.y1 * NIGHT_PX);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }
    setGlowLayer(this.glowCtx);
    const t = this.t;
    if (this.kind === "spider") {
      const S = INCOMERS.spider;
      drawSpider(this.ctx, { t, on, l, gait: Math.min(1, this.speed / S.speed), bite: c.busy ? (this.busyFor / S.biteEvery) % 1 : 0 });
    } else if (this.kind === "warden") {
      const W = INCOMERS.warden;
      drawWarden(this.ctx, { t, on, l, gait: Math.min(1, this.speed / W.speed), wind: c.busy ? Math.min(1, this.busyFor / W.windUp) : 0, follow: c.busy ? 0 : Math.max(0, 1 - this.sinceBusy / 0.5) });
    } else if (this.kind === "carrier") {
      const C = INCOMERS.carrier;
      // Lowered its spiders: empty hooks for a while, then (in the drawing) a fresh pair.
      const held = c.busy || this.sinceBusy > 6 ? 2 : 0;
      drawCarrier(this.ctx, { t, on, l, lowering: c.busy ? Math.min(1, this.busyFor / C.lower) : 0, held, sweep: Math.sin(t * 0.7) * 0.6 });
    } else drawEel(this.ctx, { t, on, l, latched: !!c.busy, speed: Math.min(1, this.speed / INCOMERS.eel.speed) });
    setGlowLayer(null);
    this.texture.needsUpdate = true;
    this.glowTexture.needsUpdate = true;
  }

  dispose(): void {
    for (const o of this.owned) o.dispose();
  }
}

/** A grapple line from a character's hand (or a ship's grapple) to where it's hooked. */
export class GrappleLine {
  readonly line: THREE.Line;
  private readonly positions = new Float32Array(6);

  constructor(color = 0xc8a060) {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(this.positions, 3));
    this.line = new THREE.Line(g, new THREE.LineBasicMaterial({ color }));
    this.line.frustumCulled = false;
  }

  set(from: THREE.Vector3, to: { x: number; y: number }): void {
    this.positions.set([from.x, from.y, 0.3, to.x, to.y, 0.3]);
    this.line.geometry.getAttribute("position").needsUpdate = true;
  }
}
